/**
 * Encounter Chart Component
 * Interactive Recharts implementation of encounter visualization with healthcare-specific features
 */

import { Card, Spin, Alert } from 'antd';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
} from 'recharts';
import { useAppSelector } from '../../../store/hooks';
import { 
  transformChartDataForRecharts, 
  createChartClickData,
  type ChartClickData 
} from '../utils/chartDataTransform';

interface EncounterChartProps {
  className?: string;
  onChartClick?: (data: ChartClickData) => void;
}

function EncounterChart({ className, onChartClick }: EncounterChartProps) {
  const { chartData, loading, error, filters } = useAppSelector(state => state.dashboard);

  // Handle chart bar click for navigation to AngularJS pages
  const handleBarClick = (data: any, barKey: string) => {
    if (!chartData || !onChartClick) return;
    
    const clickData = createChartClickData(
      data,
      barKey,
      filters.facilityId,
      filters.dateRange,
      chartData.x_axis_label
    );
    
    onChartClick(clickData);
  };

  // Custom tooltip for healthcare data
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
          {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p
            key={index}
            style={{
              margin: '4px 0',
              color: entry.color,
              fontSize: '14px'
            }}
          >
            <span style={{ fontWeight: 'bold' }}>
              {entry.name}:
            </span>
            {' '}
            <span style={{ fontWeight: 'bold' }}>
              {entry.value}
            </span>
            {' encounters'}
          </p>
        ))}
        <p style={{
          margin: '8px 0 0 0',
          paddingTop: '8px',
          borderTop: '1px solid #eee',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          Total: {payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0)} encounters
        </p>
        <p style={{
          margin: '4px 0 0 0',
          fontSize: '12px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Click to view patient list
        </p>
      </div>
    );
  };

  if (loading.chartData) {
    return (
      <Card className={className}>
        <Spin size="large" tip="Loading encounter data...">
          <div style={{ height: '500px' }} />
        </Spin>
      </Card>
    );
  }

  if (error.chartData) {
    return (
      <Card className={className}>
        <Alert
          message="Chart Error"
          description={error.chartData}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!chartData) {
    return (
      <Card className={className}>
        <div style={{ 
          height: '500px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#666'
        }}>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>
            No encounter data available
          </p>
          <p style={{ fontSize: '14px' }}>
            Select filters and click Search to load chart data
          </p>
        </div>
      </Card>
    );
  }

  // Transform data for Recharts
  const { data, metrics } = transformChartDataForRecharts(chartData);

  if (data.length === 0 || metrics.length === 0) {
    return (
      <Card className={className}>
        <div style={{ 
          height: '500px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: '#666'
        }}>
          <p>No chart data available for the selected date range and facility</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <div style={{ height: '500px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 40, right: 20, left: 30, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 10 }}
              angle={0}
              textAnchor="middle"
              height={30}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 10 }}
              width={40}
            />
            <RechartsTooltip 
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
            />
            <Legend 
              wrapperStyle={{ 
                fontSize: '11px', 
                paddingTop: '0px',
                paddingBottom: '10px'
              }}
              iconSize={10}
              align="center"
              verticalAlign="top"
            />
            
            {/* Render bars for each metric */}
            {metrics.map((metric) => (
              <Bar
                key={metric.key}
                dataKey={metric.key}
                name={metric.label}
                stackId={metric.stackId}
                fill={metric.color}
                onClick={(data) => handleBarClick(data, metric.key)}
                style={{ cursor: onChartClick ? 'pointer' : 'default' }}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default EncounterChart;